import { useContext } from 'react';
import { NavigateOptions } from '@reach/router';
import { navigate as gatsbyNavigate } from 'gatsby';
import { I18nextContext, useI18next } from 'gatsby-plugin-react-i18next';
import { getLocalUrlIfLiveOneUsed, isInternalLink, removeLanguageIfInPath } from './language';

/**
 * A custom hook that provides a `navigateWrapper` function, leveraging the `navigate` function from
 * `gatsby-plugin-react-i18next` for navigation with internationalization support. This setup allows
 * for programmatically navigating to different routes within your Gatsby application while
 * utilizing the i18n features, facilitating development of multi-lingual sites.
 *
 * @returns An object containing the `navigateWrapper` function which can be used to perform navigation.
 *          The `navigateWrapper` function is detailed below:
 *          @param {string} to The path to navigate to. This should be a relative path starting with '/'.
 *                             For example, to navigate to the home page, you would pass '/' as the argument.
 *          @param {NavigateOptions<{}> | undefined} [options] Optional. Configuration options for navigation.
 *                              This can include state to pass to the navigation target, a replace flag to
 *                              replace the current entry in the history stack, and a preventScrollReset to
 *                              control the scroll behavior after navigation.
 */
const useNavigate = () => {
  const { navigate } = useI18next();
  const context = useContext(I18nextContext);
  const { defaultLanguage } = context;

  const navigateWrapper = (to: string, options?: NavigateOptions<{}> | undefined) => {
    const finalUrl = removeLanguageIfInPath(getLocalUrlIfLiveOneUsed(to), defaultLanguage);
    const isInternal = isInternalLink(finalUrl);

    if (isInternal) {
      navigate(finalUrl, options);
    } else {
      gatsbyNavigate(finalUrl, options);
    }
  };

  return {
    navigateWrapper
  };
};

export default useNavigate;
